import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import type { ProductLine } from "./types";

// SHARED product-first field-set (Task #158 + INTAKE-VEHICLE #144). The single source
// of the product-aware intake fields per the canonical [KAPI] doc
// (product/specs/urun-bazli-bilgi-ihtiyaci.md): Trafik/Kasko → vehicle (plate/ruhsat/…),
// DASK → dwelling (adres/UAVT/…), Seyahat → travel (ülke/tarih/kişi), Sağlık → SGK/hastane.
// Extracted from phone-intake so the "Müşteri → Teklif Formu" wizard reuses the SAME
// field set + validation + payload mapping instead of forking a divergent one.

const PLATE_RE = /^[0-9]{2}\s?[A-Z]{1,3}\s?[0-9]{2,4}$/;
const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/;
const UAVT_RE = /^\d{10}$/;

/** Lines that map to a VEHICLE asset (plate/vin). */
export const VEHICLE_LINES: ReadonlySet<ProductLine> = new Set<ProductLine>(["trafik", "kasko"]);
/** The five pilot products, product-first order. */
export const PRODUCT_LINES: readonly ProductLine[] = [
  "trafik",
  "kasko",
  "dask",
  "seyahat",
  "saglik",
];

/** Every product-specific field (all string-backed for the form). Common caller basics
 *  (TC, ad soyad, cep, e-posta, KVKK) live on the surface, NOT here. */
export interface ProductFieldValues {
  // Vehicle (Trafik/Kasko, canonical §1–2).
  plate: string;
  make: string;
  model: string;
  vin: string;
  ruhsatSerno: string;
  ehliyetDate: string;
  // Dwelling (DASK, canonical §3).
  address: string;
  uavtCode: string;
  brutM2: string;
  yapiTarzi: string;
  insaYili: string;
  kat: string;
  // Travel (Seyahat, canonical §4).
  travelDestination: string;
  travelStart: string;
  travelEnd: string;
  travelers: string;
  // Health (Sağlık, canonical §5 — SGK + hospital only; NO health declaration).
  sgkStatus: string;
  hospitalPref: string;
}

export const emptyProductFields = (): ProductFieldValues => ({
  plate: "",
  make: "",
  model: "",
  vin: "",
  ruhsatSerno: "",
  ehliyetDate: "",
  address: "",
  uavtCode: "",
  brutM2: "",
  yapiTarzi: "",
  insaYili: "",
  kat: "",
  travelDestination: "",
  travelStart: "",
  travelEnd: "",
  travelers: "",
  sgkStatus: "",
  hospitalPref: "",
});

export interface ProductFieldErrors {
  plateInvalid: boolean;
  vinInvalid: boolean;
  uavtInvalid: boolean;
}

/** Optional-but-encouraged: an empty value is fine; a filled value must be valid. */
export function productFieldErrors(line: ProductLine, v: ProductFieldValues): ProductFieldErrors {
  const isVehicleLine = VEHICLE_LINES.has(line);
  const isDask = line === "dask";
  const plateTrimmed = v.plate.trim().toUpperCase();
  const vinTrimmed = v.vin.trim().toUpperCase();
  const uavtTrimmed = v.uavtCode.trim();
  return {
    plateInvalid: isVehicleLine && plateTrimmed.length > 0 && !PLATE_RE.test(plateTrimmed),
    vinInvalid: isVehicleLine && vinTrimmed.length > 0 && !VIN_RE.test(vinTrimmed),
    uavtInvalid: isDask && uavtTrimmed.length > 0 && !UAVT_RE.test(uavtTrimmed),
  };
}

/** True when any product field carries an invalid value that should block submit. */
export function productFieldsInvalid(line: ProductLine, v: ProductFieldValues): boolean {
  const e = productFieldErrors(line, v);
  return e.plateInvalid || e.vinInvalid || e.uavtInvalid;
}

/** The line-conditioned subset the capture chain accepts (a value only rides when it is
 *  meaningful for the line). Shared by phone-intake and the quote-form wizard. */
export interface ProductFieldPayload {
  plate?: string;
  make?: string;
  model?: string;
  vin?: string;
  ruhsatSerno?: string;
  ehliyetDate?: string;
  address?: string;
  uavtCode?: string;
  brutM2?: string;
  yapiTarzi?: string;
  insaYili?: string;
  kat?: string;
  travelDestination?: string;
  travelStart?: string;
  travelEnd?: string;
  travelers?: string;
  sgkStatus?: string;
  hospitalPref?: string;
}

export function productFieldsToPayload(
  line: ProductLine,
  v: ProductFieldValues,
): ProductFieldPayload {
  const isVehicleLine = VEHICLE_LINES.has(line);
  const isKasko = line === "kasko";
  const isDask = line === "dask";
  const isSeyahat = line === "seyahat";
  const isSaglik = line === "saglik";
  const s = (x: string): string | undefined => x.trim() || undefined;
  return {
    // Vehicle field-set — only meaningful for a vehicle line.
    plate: isVehicleLine ? s(v.plate) : undefined,
    make: isVehicleLine ? s(v.make) : undefined,
    model: isVehicleLine ? s(v.model) : undefined,
    vin: isVehicleLine ? s(v.vin) : undefined,
    ruhsatSerno: isVehicleLine ? s(v.ruhsatSerno) : undefined,
    ehliyetDate: isKasko ? s(v.ehliyetDate) : undefined,
    // Dwelling (DASK).
    address: isDask ? s(v.address) : undefined,
    uavtCode: isDask ? s(v.uavtCode) : undefined,
    brutM2: isDask ? s(v.brutM2) : undefined,
    yapiTarzi: isDask ? s(v.yapiTarzi) : undefined,
    insaYili: isDask ? s(v.insaYili) : undefined,
    kat: isDask ? s(v.kat) : undefined,
    // Travel.
    travelDestination: isSeyahat ? s(v.travelDestination) : undefined,
    travelStart: isSeyahat ? s(v.travelStart) : undefined,
    travelEnd: isSeyahat ? s(v.travelEnd) : undefined,
    travelers: isSeyahat ? s(v.travelers) : undefined,
    // Health — SGK/hospital only.
    sgkStatus: isSaglik ? s(v.sgkStatus) : undefined,
    hospitalPref: isSaglik ? s(v.hospitalPref) : undefined,
  };
}

/** The DASK §3 dwelling attributes createAssetSchema now accepts on the DWELLING branch
 *  (dwellingAddress/GrossM2/Structure/BuildYear/Floor). Sent alongside uavtCode + label
 *  — birebir the web door. grossM2/buildYear ride as the form's string values: the server
 *  schema is z.coerce.number() (asset.ts), so "120"/"2008" pass. Only present keys ride. */
export function dwellingAssetAttrs(v: ProductFieldPayload): Record<string, string> {
  const out: Record<string, string> = {};
  if (v.address) out.dwellingAddress = v.address;
  if (v.brutM2) out.dwellingGrossM2 = v.brutM2;
  if (v.yapiTarzi) out.dwellingStructure = v.yapiTarzi;
  if (v.insaYili) out.dwellingBuildYear = v.insaYili;
  if (v.kat) out.dwellingFloor = v.kat;
  return out;
}

/** Non-sensitive capture metadata for the ONBOARDING interaction's subjectRef (the only
 *  free-form contract slot) — the travel + SGK/hospital attrs that have NO asset/quote
 *  home, plus the health açık-rıza attestation. Mirrors the web door's buildCaptureMeta
 *  (web-intake.service): the keys are deliberately non-special-category (SGK status /
 *  hospital preference are NOT sensitive per the §5 note; the health DECLARATION is never
 *  here), so the capture jsonb sensitive-key guard passes. Null when there is nothing to
 *  ride. `healthConsent` records the advisor-attested (verbal) sağlık açık rıza. This is
 *  DELIBERATELY interaction metadata, NOT a formal `consent.consent` row — and that is the
 *  KVKK-correct posture, not a gap: açık rıza must come FROM the data subject, so
 *  `consent.service.grant` is an allowlist that refuses staff/service principals ("never by
 *  staff or service principals on their behalf"), and the formal writer
 *  `captureHealthConsent` (quote-intake.service) is reachable only from the doors where the
 *  SUBJECT self-serves (public web / authenticated customer). A phone açık rıza is verbal +
 *  advisor-captured, so its governed home is the interaction path: the ONBOARDING intent
 *  bound to the live aydınlatma notice version (B0-INT, migration 0046) + this attestation +
 *  audit (lawful_basis, config version, occurredAt) — the same legal-proof standard as the
 *  web path, adapted to verbal capture, and parallel to how the general aydınlatma + the
 *  marketing (CROSS_SELL) consent are captured here rather than as consent rows. Minting a
 *  staff-authored health consent row would VIOLATE the data-subject-only invariant. See the
 *  B0 spec §12 note. (Fuller notice-version parity with the dedicated `saglik-acik-riza`
 *  notice is a separate follow-on, not a blocker.) */
export function productCaptureMeta(
  line: ProductLine,
  v: ProductFieldPayload,
  opts: { healthConsent?: boolean } = {},
): Record<string, unknown> | null {
  const capture: Record<string, unknown> = {};
  if (line === "seyahat") {
    const trip: Record<string, unknown> = {};
    if (v.travelDestination) trip.destination = v.travelDestination;
    if (v.travelStart) trip.startDate = v.travelStart;
    if (v.travelEnd) trip.endDate = v.travelEnd;
    if (v.travelers) trip.travelers = v.travelers;
    if (Object.keys(trip).length > 0) capture.trip = trip;
  }
  if (line === "saglik") {
    const saglik: Record<string, unknown> = { declarationOffline: true };
    if (v.sgkStatus) saglik.sgkStatus = v.sgkStatus;
    if (v.hospitalPref) saglik.hospitalPref = v.hospitalPref;
    if (opts.healthConsent) saglik.saglikAcikRiza = true;
    capture.saglik = saglik;
  }
  return Object.keys(capture).length > 0 ? { product: line, capture } : null;
}

export interface ProductFieldsProps {
  line: ProductLine;
  values: ProductFieldValues;
  onChange: (patch: Partial<ProductFieldValues>) => void;
}

/** The product-aware field sections. Rendered identically in phone-intake and the
 *  quote-form Step 1 (data-testids/labels shared) so the two never diverge. */
export function ProductFields({ line, values, onChange }: ProductFieldsProps) {
  const { t } = useT();
  const isVehicleLine = VEHICLE_LINES.has(line);
  const isKasko = line === "kasko";
  const isDask = line === "dask";
  const isSeyahat = line === "seyahat";
  const isSaglik = line === "saglik";
  const { plateInvalid, vinInvalid, uavtInvalid } = productFieldErrors(line, values);

  return (
    <>
      {/* Vehicle field-set — VEHICLE line (trafik/kasko). Plate/VIN feed the asset;
          ruhsat seri no is capture-only; kasko adds the ehliyet alış tarihi. §1–2. */}
      {isVehicleLine ? (
        <div className="k-card" data-testid="vehicle-section">
          <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
            {t("intake.vehicleSection")}
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("intake.plate")}</span>
            <input
              className={`k-input${plateInvalid ? " k-input--error" : ""}`}
              data-testid="in-plate"
              autoCapitalize="characters"
              value={values.plate}
              onChange={(e) => onChange({ plate: e.target.value.toUpperCase() })}
              aria-invalid={plateInvalid || undefined}
            />
            {plateInvalid ? (
              <p className="k-field__error" role="alert" data-testid="plate-invalid">
                <Icon name="alert-triangle" size={14} />
                {t("intake.plateInvalid")}
              </p>
            ) : (
              <span className="k-field__hint">{t("intake.plateHint")}</span>
            )}
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.ruhsat")}</span>
            <input
              className="k-input"
              data-testid="in-ruhsat"
              value={values.ruhsatSerno}
              onChange={(e) => onChange({ ruhsatSerno: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.make")}</span>
            <input
              className="k-input"
              data-testid="in-make"
              value={values.make}
              onChange={(e) => onChange({ make: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.model")}</span>
            <input
              className="k-input"
              data-testid="in-model"
              value={values.model}
              onChange={(e) => onChange({ model: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.vin")}</span>
            <input
              className={`k-input${vinInvalid ? " k-input--error" : ""}`}
              data-testid="in-vin"
              autoCapitalize="characters"
              value={values.vin}
              onChange={(e) => onChange({ vin: e.target.value.toUpperCase() })}
              aria-invalid={vinInvalid || undefined}
            />
            {vinInvalid ? (
              <p className="k-field__error" role="alert" data-testid="vin-invalid">
                <Icon name="alert-triangle" size={14} />
                {t("intake.vinInvalid")}
              </p>
            ) : (
              <span className="k-field__hint">{t("intake.vinHint")}</span>
            )}
          </label>
          {isKasko ? (
            <label className="k-field">
              <span className="k-field__label">{t("intake.ehliyetDate")}</span>
              <input
                className="k-input"
                data-testid="in-ehliyet"
                type="date"
                value={values.ehliyetDate}
                onChange={(e) => onChange({ ehliyetDate: e.target.value })}
              />
            </label>
          ) : null}
        </div>
      ) : null}

      {/* Dwelling field-set — DASK (§3). UAVT + address feed a DWELLING asset; the rest
          is capture-only (createAssetSchema is .strict()). */}
      {isDask ? (
        <div className="k-card" data-testid="dwelling-section">
          <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
            {t("intake.dwellingSection")}
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("intake.address")}</span>
            <input
              className="k-input"
              data-testid="in-address"
              value={values.address}
              onChange={(e) => onChange({ address: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.uavt")}</span>
            <input
              className={`k-input${uavtInvalid ? " k-input--error" : ""}`}
              data-testid="in-uavt"
              inputMode="numeric"
              value={values.uavtCode}
              onChange={(e) =>
                onChange({ uavtCode: e.target.value.replace(/\D/g, "").slice(0, 10) })
              }
              aria-invalid={uavtInvalid || undefined}
            />
            {uavtInvalid ? (
              <p className="k-field__error" role="alert" data-testid="uavt-invalid">
                <Icon name="alert-triangle" size={14} />
                {t("intake.uavtInvalid")}
              </p>
            ) : (
              <span className="k-field__hint">{t("intake.uavtHint")}</span>
            )}
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.brutM2")}</span>
            <input
              className="k-input"
              data-testid="in-brutm2"
              inputMode="numeric"
              value={values.brutM2}
              onChange={(e) => onChange({ brutM2: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.yapiTarzi")}</span>
            <span className="k-select">
              <select
                className="k-select__native"
                data-testid="in-yapitarzi"
                value={values.yapiTarzi}
                onChange={(e) => onChange({ yapiTarzi: e.target.value })}
              >
                <option value="">{t("intake.selectPlaceholder")}</option>
                <option value="betonarme">{t("intake.yapiTarzi.betonarme")}</option>
                <option value="celik">{t("intake.yapiTarzi.celik")}</option>
                <option value="yigma">{t("intake.yapiTarzi.yigma")}</option>
                <option value="diger">{t("intake.yapiTarzi.diger")}</option>
              </select>
              <span className="k-select__chevron">
                <Icon name="chevron-down" size={16} />
              </span>
            </span>
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.insaYili")}</span>
            <input
              className="k-input"
              data-testid="in-insayili"
              inputMode="numeric"
              value={values.insaYili}
              onChange={(e) => onChange({ insaYili: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.kat")}</span>
            <input
              className="k-input"
              data-testid="in-kat"
              value={values.kat}
              onChange={(e) => onChange({ kat: e.target.value })}
            />
          </label>
          <p className="k-field__hint" data-testid="dwelling-capture-note">
            {t("intake.dwellingCaptureNote")}
          </p>
        </div>
      ) : null}

      {/* Travel field-set — Seyahat (§4). All capture-only (no asset/quote). */}
      {isSeyahat ? (
        <div className="k-card" data-testid="travel-section">
          <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
            {t("intake.travelSection")}
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("intake.travelDestination")}</span>
            <input
              className="k-input"
              data-testid="in-destination"
              value={values.travelDestination}
              onChange={(e) => onChange({ travelDestination: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.travelStart")}</span>
            <input
              className="k-input"
              data-testid="in-travel-start"
              type="date"
              value={values.travelStart}
              onChange={(e) => onChange({ travelStart: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.travelEnd")}</span>
            <input
              className="k-input"
              data-testid="in-travel-end"
              type="date"
              value={values.travelEnd}
              onChange={(e) => onChange({ travelEnd: e.target.value })}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.travelers")}</span>
            <input
              className="k-input"
              data-testid="in-travelers"
              inputMode="numeric"
              value={values.travelers}
              onChange={(e) => onChange({ travelers: e.target.value })}
            />
          </label>
          <p className="k-field__hint" data-testid="travel-capture-note">
            {t("intake.travelCaptureNote")}
          </p>
        </div>
      ) : null}

      {/* Health field-set — Sağlık (§5). SGK + hospital preference only; NO health
          declaration (special-category, offline until go-live). All capture-only. */}
      {isSaglik ? (
        <div className="k-card" data-testid="health-section">
          <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
            {t("intake.healthSection")}
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("intake.sgkStatus")}</span>
            <span className="k-select">
              <select
                className="k-select__native"
                data-testid="in-sgk"
                value={values.sgkStatus}
                onChange={(e) => onChange({ sgkStatus: e.target.value })}
              >
                <option value="">{t("intake.selectPlaceholder")}</option>
                <option value="sgkli">{t("intake.sgk.sgkli")}</option>
                <option value="sgksiz">{t("intake.sgk.sgksiz")}</option>
                <option value="bilinmiyor">{t("intake.sgk.bilinmiyor")}</option>
              </select>
              <span className="k-select__chevron">
                <Icon name="chevron-down" size={16} />
              </span>
            </span>
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.hospitalPref")}</span>
            <input
              className="k-input"
              data-testid="in-hospital"
              value={values.hospitalPref}
              onChange={(e) => onChange({ hospitalPref: e.target.value })}
            />
          </label>
          <p className="k-field__hint" data-testid="health-offline-note">
            {t("intake.healthOfflineNote")}
          </p>
        </div>
      ) : null}
    </>
  );
}
